// schedule.ts — translate between a homeowner-friendly schedule picker and the
// 5-field cron_expr the server stores. Cron is wall-clock in the Routine's
// timezone, so the hour/minute in the expression IS the local time to show — no
// timezone math here.

import { parseServerTime } from './runFormat'

export type Frequency = 'daily' | 'weekdays' | 'weekly' | 'hourly' | 'advanced'

export type ScheduleModel =
  | { freq: 'daily'; hour: number; minute: number }
  | { freq: 'weekdays'; hour: number; minute: number }
  | { freq: 'weekly'; dow: number; hour: number; minute: number }
  | { freq: 'hourly'; minute: number }
  | { freq: 'advanced'; cron: string }

export const DOW_LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

// The device's IANA timezone, used to prefill a new Routine.
export function deviceTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

export function modelToCron(m: ScheduleModel): string {
  switch (m.freq) {
    case 'daily':
      return `${m.minute} ${m.hour} * * *`
    case 'weekdays':
      return `${m.minute} ${m.hour} * * 1-5`
    case 'weekly':
      return `${m.minute} ${m.hour} * * ${m.dow}`
    case 'hourly':
      return `${m.minute} * * * *`
    case 'advanced':
      return m.cron
  }
}

// Best-effort parse of a cron string back into the structured picker. Anything
// that isn't one of the shapes modelToCron emits falls back to 'advanced' so
// the raw expression stays editable and round-trips losslessly.
export function cronToModel(cron: string): ScheduleModel {
  const f = cron.trim().split(/\s+/)
  if (f.length !== 5) return { freq: 'advanced', cron }
  const [min, hr, dom, mon, dow] = f
  const minute = Number(min)
  const hour = Number(hr)
  const isInt = (s: string) => /^\d+$/.test(s)

  if (dom === '*' && mon === '*') {
    if (dow === '*' && isInt(min) && isInt(hr)) {
      return { freq: 'daily', hour, minute }
    }
    if (dow === '1-5' && isInt(min) && isInt(hr)) {
      return { freq: 'weekdays', hour, minute }
    }
    if (isInt(dow) && isInt(min) && isInt(hr)) {
      return { freq: 'weekly', dow: Number(dow), hour, minute }
    }
    if (dow === '*' && isInt(min) && hr === '*') {
      return { freq: 'hourly', minute }
    }
  }
  return { freq: 'advanced', cron }
}

function fmtTime(hour: number, minute: number): string {
  const h12 = hour % 12 === 0 ? 12 : hour % 12
  const ampm = hour < 12 ? 'AM' : 'PM'
  return `${h12}:${String(minute).padStart(2, '0')} ${ampm}`
}

// Human-readable cadence for a cron expression, e.g. "Every day at 5:00 AM".
export function cronToText(cron: string): string {
  const m = cronToModel(cron)
  switch (m.freq) {
    case 'daily':
      return `Every day at ${fmtTime(m.hour, m.minute)}`
    case 'weekdays':
      return `Weekdays at ${fmtTime(m.hour, m.minute)}`
    case 'weekly':
      return `Every ${DOW_LABELS[m.dow] ?? 'week'} at ${fmtTime(m.hour, m.minute)}`
    case 'hourly':
      return m.minute === 0 ? 'Every hour, on the hour' : `Every hour at :${String(m.minute).padStart(2, '0')}`
    case 'advanced':
      return `Custom (${m.cron})`
  }
}

// "14:05" (an <input type=time> value) → {hour, minute}. Empty/garbage → 0,0.
export function parseTimeValue(v: string): { hour: number; minute: number } {
  const [h, mm] = v.split(':')
  const hour = Number(h)
  const minute = Number(mm)
  return {
    hour: Number.isFinite(hour) ? Math.max(0, Math.min(23, hour)) : 0,
    minute: Number.isFinite(minute) ? Math.max(0, Math.min(59, minute)) : 0,
  }
}

// {hour, minute} → "HH:MM" for an <input type=time> value.
export function toTimeValue(hour: number, minute: number): string {
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
}

// =============================================================================
// End conditions
// =============================================================================
//
// The picker offers a DATE ("ends on the 14th"), but the server stores an
// instant. Resolving one to the other needs the Routine's timezone, and the
// client is the side that has the calendar context — so it converts here and
// sends an instant, rather than making the server redo this against a tz string.
//
// "Ends on the 14th" means the 14th is included: the end instant is the last
// moment of that day, local to the Routine.

// zoneOffsetMinutes returns how far ahead of UTC `timeZone` is at `instant`.
// Derived by formatting the instant in that zone and reading the wall-clock
// back — the only way to ask the platform this without a tz library.
function zoneOffsetMinutes(instant: Date, timeZone: string): number {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
  const p: Record<string, number> = {}
  for (const { type, value } of dtf.formatToParts(instant)) {
    if (type !== 'literal') p[type] = Number(value)
  }
  // Intl renders midnight as hour 24 in some engines; normalise so Date.UTC
  // doesn't roll the day forward.
  const hour = p.hour === 24 ? 0 : p.hour
  const asIfUTC = Date.UTC(p.year, p.month - 1, p.day, hour, p.minute, p.second)
  return (asIfUTC - instant.getTime()) / 60000
}

// endOfDayInZone converts "YYYY-MM-DD" to the RFC3339 instant at 23:59:59 local
// to timeZone on that date. Returns '' for an empty or malformed date.
//
// Two passes: the first offset is read at the naive guess, the second at the
// instant that guess implies. Without the second pass a date on the far side of
// a DST change lands an hour out — which for an end date means a Routine that
// stops a day early or late.
export function endOfDayInZone(date: string, timeZone: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return ''
  const [y, m, d] = date.split('-').map(Number)
  const naive = Date.UTC(y, m - 1, d, 23, 59, 59)
  let instant = naive - zoneOffsetMinutes(new Date(naive), timeZone) * 60000
  instant = naive - zoneOffsetMinutes(new Date(instant), timeZone) * 60000
  return new Date(instant).toISOString()
}

// zonedDateValue is the inverse, for seeding the date picker from a stored
// instant: the calendar date that instant falls on, in timeZone. Going through
// the local date instead would show the wrong day for anyone whose phone is in
// a different zone from the Routine.
export function zonedDateValue(iso: string, timeZone: string): string {
  const ms = parseServerTime(iso)
  if (Number.isNaN(ms)) return ''
  const t = new Date(ms)
  const dtf = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
  return dtf.format(t) // en-CA renders as YYYY-MM-DD
}

// tomorrowInZone seeds the date picker when someone first chooses "On date".
// Today would frequently be already past — the Routine would be born finished.
export function tomorrowInZone(timeZone: string): string {
  return zonedDateValue(new Date(Date.now() + 86400000).toISOString(), timeZone)
}
